
import { formatNtcDate, formatNtcTime, getEllapsedTime, parseNtcDate } from "../Utils";
import apiClient, { ITicketData, ITIcketDataFilter, IVerifyTicketData, IVerifyTicketResponse } from "./ApiClient";
import { db, IScanData, TicketVerifyMode, ValidationStatus } from "./GateKeeperDb";
import { networkMonitor } from "./NetworkMonitor";
import { scanHistory } from "./ScanHistory";
import session from "./Session";


interface IScanToSyncInfo {
    totalOffline: number;
    totalValidToSync: number;
    totalAlreadySync: number;
    ticketsToSync: IScanData[];
}

class TicketValidator {

    private _ticketMap: Record<string, Record<string, ITicketData>> = { }

    async verifyAsync(data: IVerifyTicketData) {

        let result: IVerifyTicketResponse[];
        let mode: TicketVerifyMode;

        console.log("Verify", JSON.stringify(data));

        let isOnline = await networkMonitor.checkNetworkAsync();

        if (isOnline) {
            try {
                result = await apiClient.verifyTicketAsync(data);
                mode = TicketVerifyMode.Online;
            }
            catch (ex) {
                isOnline = await networkMonitor.checkNetworkAsync(true);
                if (isOnline)
                    throw ex;
            }
        }

        if (!isOnline) {
            const offResult = await this.verifyOfflineAsync(data);
            if (offResult) {
                offResult.isOffline = true;
                result = [offResult];
            }
            mode = TicketVerifyMode.Offline;
        }

        if (!result)
            return [];

        let i = 0;
        for (const item of result) {

            await scanHistory.addAsync({
                timestamp: new Date(new Date().getTime() + i),
                code: data.barcode,
                eventId: data.eventID,
                status: item.type == "OK" ? ValidationStatus.Valid : ValidationStatus.NotValid,
                verify: item,
                gate: data.ref_gate,
                verifyMode: mode,
            });
            i++;
        }

        return result;
    }

    async searchTicketsAsync(filter: ITIcketDataFilter) {

        const isOnline = await networkMonitor.checkNetworkAsync();

        if (isOnline)
            return apiClient.getTicketDataAsync(filter);

        return this.searchTicketsOfflineAsync(filter);
    }

    async getOfflineListAsync(eventId: string) {

        return await db.ticketList.where({ eventId: eventId }).first();
    }

    protected async searchTicketsOfflineAsync(filter: ITIcketDataFilter) {

        let ticketList = await this.getOfflineListAsync(filter.eventID);
        if (!ticketList)
            return [];

        if (!filter.searchString || filter.searchString.length == 0)
            return ticketList.tickets;

        if (filter.searchMethod == "barcode")
            return ticketList.tickets.filter(a => a.barcode && a.barcode.indexOf(filter.searchString) != -1);

        if (filter.searchMethod == "order_number")
            return ticketList.tickets.filter(a => a.riferimentoordine && a.riferimentoordine.indexOf(filter.searchString) != -1);

        if (filter.searchMethod == "ticket_holder")
            return ticketList.tickets.filter(a => ((a.nome ?? "") + " " + (a.cognome ?? "")).indexOf(filter.searchString) != -1);
    }

    async syncValidTicketsAsync(eventId: string) {

        var newTickets = await apiClient.getWhiteListAsync(eventId);

        await db.ticketList.put({
            eventId,
            lastUpdateTime: new Date(),
            tickets: newTickets
        });

        delete this._ticketMap[eventId];
    }

    protected async findOfflineTicketAsync(eventId: string, barcode: string) {

        if (!(eventId in this._ticketMap)){
            const offlineList = await this.getOfflineListAsync(eventId);
            if (!offlineList)
                return;
            const map: Record<string, ITicketData> = {};
            for (const ticket of offlineList.tickets)
                map[ticket.barcode] = ticket;
            this._ticketMap[eventId] = map;
        }

        const result = this._ticketMap[eventId][barcode];

        return result;
    }

    async getScansToSyncAsync(eventId: string) {

        const ticketList = await db.scanHistory.where({
            "eventId": eventId,
        }).toArray();

        const toSync = ticketList.filter(a => a.verifyMode == TicketVerifyMode.Offline && (!a.syncSucceeded || !a.syncTime));

        return {
            ticketsToSync: toSync,
            totalValidToSync: toSync.filter(a => a.verify?.type == "OK").length,
            totalAlreadySync: ticketList.filter(a=> a.syncTime != null && a.syncSucceeded).length,
            totalOffline: ticketList.filter(a => a.verifyMode == TicketVerifyMode.Offline).length
        } as IScanToSyncInfo;

    }

    async syncScansAsync(eventId: string, onSync?: (ticket: IScanData, result: IVerifyTicketResponse) => boolean) {

        var info = await this.getScansToSyncAsync(eventId);

        for (const ticket of info.ticketsToSync) {

            let result: IVerifyTicketResponse;

            ticket.syncTime = new Date();

            try {
                result = (await apiClient.verifyTicketAsync({
                    barcode: ticket.code,
                    direction: "E",
                    eventID: eventId,
                    ref_device: session.deviceId,
                    ref_gate: ticket.gate,
                    log_date: formatNtcDate(ticket.timestamp),
                    log_time: formatNtcTime(ticket.timestamp, true),

                }))[0];

                if (!result)
                    ticket.syncSucceeded = false;
                else {
                    ticket.syncSucceeded = true;
                    ticket.verify = result;
                    ticket.verifyMode = TicketVerifyMode.Online;
                }
            }
            catch (ex) {
                ticket.syncSucceeded = false;
            }

            await db.scanHistory.put(ticket);

            if (onSync && !onSync(ticket, result))
                break;
        }
    }

    protected async verifyOfflineAsync(data: IVerifyTicketData): Promise<IVerifyTicketResponse> {

        const formatMessage = (ticket: ITicketData) => {
            if (!ticket)
                return "";

            let msg = (ticket.nome ?? "") + (ticket.cognome ? " " + ticket.cognome : "");
            if (ticket.fila?.length > 0)
                msg += " - " + "Fila " + ticket.fila;
            if (ticket.posto?.length > 0)
                msg += ", " + "Posto " + ticket.posto;
            return msg;
        }

        const ticket = await this.findOfflineTicketAsync(data.eventID, data.barcode);

        if (!ticket && !(data.eventID in this._ticketMap))
            return {
                code: "9xx",
                msg: "Lista offline non caricata",
                msg_data: "",
                type: "KO",
            };


        const historyEntry = (await db.scanHistory
            .where({ "eventId": data.eventID, "code": data.barcode })
            .sortBy("timestamp"))[0];

        if (historyEntry) {
            if (historyEntry.verify.type == "KO")
                return historyEntry.verify;

            return {
                code: "990",
                msg: "Entrato " + getEllapsedTime(new Date(), historyEntry.timestamp),
                msg_data: formatMessage(ticket),
                type: "KO"
            }
        }


        if (ticket) {

            if (ticket.stato == "AD" || ticket.stato == "AT") {
                return {
                    code: "9xx",
                    msg: "Titolo Annullato",
                    msg_data: "",
                    type: "KO"
                };
            }

            if (ticket.stato == "ZD" || ticket.stato == "ZT") {
                const enterTime = parseNtcDate(ticket.dataingressocontrollo, ticket.oraingressocontrollo);
                return {
                    code: "990",
                    msg: "Entrato " + getEllapsedTime(new Date(), enterTime), 
                    msg_data: formatMessage(ticket),
                    type: "KO"
                }
            }

            /*
            const now = new Date();
            ticket.dataingressocontrollo = formatNtcDate(now);
            ticket.oraingressocontrollo = formatNtcTime(now);
            await db.ticketList.update(data.eventID, { tickets: offlineList.tickets });
            */

            return {
                code: "110",
                msg: "OK Biglietto Valido",
                msg_data: formatMessage(ticket),
                type: "OK"
            };
        }

        return {
            code: "950",
            msg: "Titolo Non In Lista",
            type: "KO",
            msg_data: ""
        }
    }

}


export const ticketValidator = new TicketValidator();